import { create } from 'zustand';
import { TimeSlot, TimetableData } from '../types/timetable';
import { StorageService } from '../services/StorageService';
import { ICSService } from '../services/ICSService';
import { scheduleClassNotifications, cancelClassNotification, clearAllNotifications } from '../services/notifications';
import { importTimetableFromICS as importDefaultTimetable } from '../services/ICSService';

interface TimetableStore {
  timetable: TimetableData;
  loading: boolean;
  initialize: () => Promise<void>;
  updateTimetable: (newTimetable: TimetableData) => Promise<void>;
  addClass: (day: string, classData: TimeSlot) => Promise<void>;
  updateClass: (day: string, classData: TimeSlot) => Promise<void>;
  deleteClass: (day: string, classId: string) => Promise<void>;
  importDefaultTimetable: () => Promise<void>;
}

const defaultTimetable: TimetableData = {
  Mon: [],
  Tue: [],
  Wed: [],
  Thu: [],
  Fri: [],
};

export const useTimetableStore = create<TimetableStore>((set, get) => ({
  timetable: defaultTimetable,
  loading: false,

  initialize: async () => {
    set({ loading: true });
    try {
      const savedTimetable = await StorageService.loadTimetable();
      if (savedTimetable) {
        set({ timetable: savedTimetable });
      }
    } catch (error) {
      console.error('Error initializing timetable:', error);
    } finally {
      set({ loading: false });
    }
  },

  importDefaultTimetable: async () => {
    set({ loading: true });
    try {
      await clearAllNotifications();
      const newTimetable = await importDefaultTimetable();
      await StorageService.saveTimetable(newTimetable);
      set({ timetable: newTimetable });

      // Schedule notifications for all imported classes
      for (const day in newTimetable) {
        for (const classData of newTimetable[day]) {
          // Enable notifications by default for imported classes
          const classWithNotifications = {
            ...classData,
            notifications: {
              beforeStart: true,
              beforeEnd: true
            }
          };
          await scheduleClassNotifications(classWithNotifications);
        }
      }

      console.log('✅ Successfully imported timetable and scheduled all notifications');
    } catch (error) {
      console.error('Error importing default timetable:', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateTimetable: async (newTimetable) => {
    try {
      await StorageService.saveTimetable(newTimetable);
      set({ timetable: newTimetable });
    } catch (error) {
      console.error('Error updating timetable:', error);
    }
  },

  addClass: async (day, classData) => {
    try {
      await StorageService.addClass(day, classData);
      const newTimetable = { ...get().timetable };
      if (!newTimetable[day]) {
        newTimetable[day] = [];
      }
      newTimetable[day].push(classData);
      set({ timetable: newTimetable });

      // Schedule notifications for the new class
      if (classData.notifications?.beforeStart || classData.notifications?.beforeEnd) {
        await scheduleClassNotifications(classData);
      }
    } catch (error) {
      console.error('Error adding class:', error);
    }
  },

  updateClass: async (day, classData) => {
    try {
      await StorageService.updateClass(day, classData);
      const newTimetable = { ...get().timetable };
      if (newTimetable[day]) {
        newTimetable[day] = newTimetable[day].map((item) =>
          item.id === classData.id ? classData : item
        );
        set({ timetable: newTimetable });

        // Schedule notifications for the updated class
        if (classData.notifications?.beforeStart || classData.notifications?.beforeEnd) {
          await scheduleClassNotifications(classData);
        }
      }
    } catch (error) {
      console.error('Error updating class:', error);
    }
  },

  deleteClass: async (day, classId) => {
    try {
      await StorageService.deleteClass(day, classId);
      const newTimetable = { ...get().timetable };
      if (newTimetable[day]) {
        // Cancel notifications before removing the class
        const classToDelete = newTimetable[day].find(item => item.id === classId);
        if (classToDelete) {
          await cancelClassNotification(classId);
        }
        
        // Remove the class from timetable
        newTimetable[day] = newTimetable[day].filter((item) => item.id !== classId);
        set({ timetable: newTimetable });
      }
    } catch (error) {
      console.error('Error deleting class:', error);
    }
  },
}));
